/**
 * Queue to track loaded scripts
 */
const scriptsQueue = [];

/**
 * Constant for the data attribute name used for script dependencies
 */
const DATA_SCRIPT_DEPENDENCIES = 'data-script-dependencies';

/**
 * Loads a script if it hasn't been loaded yet
 * @param {string} src - The source URL of the script
 */
const loadScript = (src) => {
	if (!scriptsQueue.includes(src) && !document.querySelector(`script[src="${src}"]`)) {
		const scriptTag = document.createElement('script');
		scriptTag.src = src;
		scriptTag.onerror = () => console.error(`Failed to load script: ${src}`);
		document.body.appendChild(scriptTag);
		scriptsQueue.push(src);
	}
};

/**
 * Loads scripts when elements intersect with the viewport
 * @param {Element[]} elements - Elements to observe
 * @param {string|string[]} scriptSources - Script source(s) to load
 * @param {number} threshold - Intersection threshold
 * @param {string} rootMargin - Intersection root margin
 */
export const loadScriptWhenIntersecting = (elements, scriptSources, threshold = 0, rootMargin = '400px') => {
	if (elements.length) {
		const observer = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (Array.isArray(scriptSources)) {
							scriptSources.forEach(loadScript);
						} else {
							loadScript(scriptSources);
						}
						observer.unobserve(entry.target);
						entry.target.removeAttribute(DATA_SCRIPT_DEPENDENCIES);
					}
				});
			},
			{
				threshold,
				rootMargin,
			},
		);
		elements.forEach((el) => observer.observe(el));
	}
};

/**
 * Registers script dependencies for elements with the designated data attribute
 */
export const registerScriptDependencies = () => {
	const elements = document.querySelectorAll(`[${DATA_SCRIPT_DEPENDENCIES}]`);
	elements.forEach((el) => {
		const dataValue = el.getAttribute(DATA_SCRIPT_DEPENDENCIES);
		const dependencies = dataValue
			.split(',')
			.map((dep) => dep.trim())
			.filter(Boolean);
		loadScriptWhenIntersecting([el], dependencies);
	});
};
